/**
 * THE OPT-IN VOCABULARY, IN ONE PLACE.
 *
 * Four surfaces now report the pilot's opt-in position: the roster's Response column, the
 * roster's summary strip, the card on the CoFitter dashboard and the card on Admin →
 * Overview. Two admin surfaces disagreeing about "education status" is the exact fault this
 * consolidation was opened to fix, so the words and the arithmetic live here once and every
 * surface imports them. A surface cannot phrase it differently because it has no phrasing of
 * its own.
 *
 * Everything here is pure and derived from the payload of GET /api/education/admin/tracker.
 */

/** The plain words. `in` / `—` was unreadable at a glance; these are what Randall asked for. */
export const OPTED_IN = "Opted in";
export const OPTED_OUT = "Opted out";
export const NO_RESPONSE = "No response yet";
export const NOT_SENT = "Not sent yet";
export const YOU_NOT_SENT = "you — not sent";
export const LINK_USED = "Link used";
export const NO_SYSTEM_SEND = "link, no system send";

/**
 * Did an invitation reach this person, whichever way it travelled? The same evidence the
 * server's `not_sent` counts: an enrollment or a used invite proves it did. `invite_sent` alone
 * says only that a send went through our code, and a link handed over by hand leaves it false.
 */
const reachedWithoutSend = (row) => !row.invite_sent && !!(row.enrolled || row.invite_used);

/**
 * What one roster row's Sent cell says. A SYSTEM SEND AND A LINK HANDED OVER STAY DIFFERENT: only
 * a send through our code gets a date. `invite_sent_at` is not read without `invite_sent`, because
 * a re-issue can leave the date of a dead link on the row.
 */
export function sentStatus(row = {}) {
  if (row.invite_sent) return { key: "system", at: row.invite_sent_at };
  if (reachedWithoutSend(row)) return { key: "no_system_send", label: NO_SYSTEM_SEND, tone: "good" };
  return { key: "not_sent", label: "not sent", tone: "idle" };
}

/**
 * What one roster row's Response cell says, and what colour it carries.
 *
 * NOT-SENT IS NOT SILENCE. A row whose invite was never sent has not failed to answer —
 * nobody asked it. Randall's own row is the live case: he is staged but unsent, and rendering
 * it as "no response" would read as though he had ignored his own pilot. The server already
 * keeps these apart in its counts (`not_sent` vs `no_response`); this keeps them apart on
 * the page.
 *
 * @param row          one entry of the tracker's `roster`
 * @param viewerEmail  the signed-in admin's address, so their own unsent row says so
 */
export function responseStatus(row = {}, viewerEmail = "") {
  const mine =
    !!viewerEmail &&
    (row.email || "").trim().toLowerCase() === viewerEmail.trim().toLowerCase();

  const reached = reachedWithoutSend(row);
  if (!row.invite_sent && !reached) {
    return { key: "not_sent", label: mine ? YOU_NOT_SENT : NOT_SENT, tone: "idle" };
  }
  if (row.response === "yes") return { key: "in", label: OPTED_IN, tone: "good" };
  if (row.response === "no") return { key: "out", label: OPTED_OUT, tone: "bad" };
  if (reached) return { key: "link_used", label: LINK_USED, tone: "good" };
  return { key: "no_response", label: NO_RESPONSE, tone: "wait" };
}

/**
 * Why a row cannot move, as a sentence rather than a field list.
 *
 * `blocked_on` has been carried in the tracker payload since the queue was staged and has
 * never been rendered anywhere — three of the four Australian rows were staged with no email
 * address at all, which is precisely why their invites are unsent. Naming the reason is the
 * difference between "needs attention" and an admin re-reading a spreadsheet to find out why.
 */
export function blockedReason(row = {}) {
  const blocked = row.blocked_on || [];
  if (!blocked.length) return "";
  const words = blocked.map((b) => String(b).replace(/_/g, " "));
  return words.length === 1
    ? `waiting on ${words[0]}`
    : `waiting on ${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
}

/**
 * The whole pilot in four numbers, summed from the SERVER's per-group summaries rather than
 * recounted from the roster — so the strip, the two cards and the per-group headers are the
 * same arithmetic and cannot drift apart.
 *
 * RETURNS null WHEN THERE IS NOTHING TO SUM, NEVER ZEROS. A payload with no `summary` means
 * the shape changed or the read failed; answering "0 opted in" to that is a confident wrong
 * answer that reads exactly like a real, empty pilot. `null` makes the caller say it could
 * not read rather than report an absence it never measured.
 */
export function rollUp(data) {
  const summaries = (data && data.summary) || [];
  if (!Array.isArray(summaries) || !summaries.length) return null;

  const add = (field) =>
    summaries.reduce((n, s) => n + (Number.isFinite(s?.[field]) ? s[field] : 0), 0);

  return {
    people: add("people"),
    invited: add("invited"),
    opted_in: add("opted_in"),
    opted_out: add("opted_out"),
    no_response: add("no_response"),
    // NEEDS ATTENTION IS WHAT WE OWE, NOT WHAT THEY OWE. An unsent invite is ours to fix;
    // silence after a sent invite is theirs to answer, and it is counted separately above.
    //
    // BOUNCES ARE NOT IN THIS NUMBER, AND CANNOT BE. `edu_enrollment_queue` records that an
    // invite was sent and when; it has no delivery-outcome field, and no code writes one. A
    // hard bounce would still read as "invite sent · no response yet" here. Saying so is the
    // honest version of a count that would otherwise imply it covers delivery failures.
    needs_attention: add("not_sent"),
  };
}

/** The strip, in the order an admin reads it: the good news, the silence, then the work. */
export function summaryCells(roll) {
  if (!roll) return [];
  return [
    { key: "opted_in", label: OPTED_IN, tone: "good", value: roll.opted_in },
    { key: "no_response", label: NO_RESPONSE, tone: "wait", value: roll.no_response },
    { key: "opted_out", label: OPTED_OUT, tone: "bad", value: roll.opted_out },
    {
      key: "needs_attention",
      label: "Needs attention",
      detail: "invite not sent",
      tone: roll.needs_attention > 0 ? "bad" : "idle",
      value: roll.needs_attention,
    },
  ];
}
